import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "toast/toastHelper";
import {
  deleteFile, getknowlageBaseInfo, knowlageBase, knowlageBaseEdit,
  uploadFile, uploadLogo,
} from "./services/knowlageBaseInfo";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import Header from "units/dashboard/components/Header";
import Footer from "units/dashboard/components/Footer";
import UserSidebar from "./components/UserSidebar";
import config from "../../config.js";
import { ProgressBar } from "react-loader-spinner";
import {
  BookOpen, Upload, Trash2, Download, Image as ImageIcon,
  FileText, Link2, CreditCard, CheckCircle2, Clock, XCircle, Loader2, Sparkles, Save,
} from "lucide-react";
import { getQrCode } from "./services/qrcode";
import { QrCode } from "lucide-react";
import { useTranslation } from "react-i18next";

const CreatingKnowledgeBaseInner = () => {
  const { t } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("empty");
  const [prompt, setPrompt] = useState("");
  const [website, setWebsite] = useState("");
  const [logo, setLogo] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(0);
  const [files, setFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [planLimitations, setPlanLimitations] = useState({});
  const [customerPaymentEnabled, setCustomerPaymentEnabled] = useState(false);
  const [customerTokenPrice, setCustomerTokenPrice] = useState("");
  const fileLogoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const BASE_URL = config.cdnUrl;
  const [fileUrl, setFileUrl] = useState([]);
  const [knowledgeBaseFileUrls, setKnowledgeBaseFileUrls] = useState([]);
  const [username, setUsername] = useState("");
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) {
        toggleSidebar();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  /* ---- LOGIC دست‌نخورده ---- */
  const getInfo = async () => {
    try {
      const res = await getknowlageBaseInfo(accessToken);
      if (res?.ok) {
        setStatus(res?.data?.status);
        setPlanLimitations(res?.data?.plan);
        setLogo(res?.data?.logo);
        setUsername(res?.data?.username ?? "");
        setPrompt(res?.data?.prompt);
        setWebsite(res?.data?.website);
        setFiles(res?.data?.knowledgeBaseFiles);
        setCustomerPaymentEnabled(res?.data?.customerPaymentEnabled ?? false);
        setCustomerTokenPrice(res?.data?.customerTokenPrice ?? "");
        setFileUrl(res.data.filelist.filter((file) => file.url !== res.data.logo).map((f) => f.url));
        setKnowledgeBaseFileUrls(
          (res.data.knowledgeBaseFiles || []).filter((f) => f.url !== res.data.logo).map((f) => f.url)
        );
      }
    } catch (error) {
      console.log("خطا در دریافت اطلاعات:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getInfo(); }, [accessToken]);
  const fetchQrCode = async () => {
    if (!username) return;

    try {
      setQrLoading(true);

      const blob = await getQrCode({
        accessToken,
        username,
      });

      console.log("QR Blob --->", blob);

      if (blob instanceof Blob) {
        const imageUrl = URL.createObjectURL(blob);

        setQrData(imageUrl);

      } else {
        showToast(t("knowledgeBase.qr.error"), "error");
      }
    } catch (error) {
      console.error("QR Code Error:", error);

      showToast(
        error?.message || t("knowledgeBase.qr.error"),
        "error"
      );
    } finally {
      setQrLoading(false);
    }
  };

  useEffect(() => { fetchQrCode(); }, [username, accessToken]);

  // پشتیبانی از هر دو حالت: base64 / URL کامل / مسیر نسبی
  const qrSrc = (() => {
    const v = qrData?.qrCode || qrData?.url;
    if (!v) return "";
    return v.startsWith("data:") || v.startsWith("http") ? v : `${BASE_URL}${v}`;
  })();
  const MAX_LOGO_SIZE = Infinity;
  const handleLogoFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      const allowedFormats = ["image/jpeg", "image/png", "image/gif"];
      if (file.name.split(".").length - 1 > 1) {
        showToast(t("knowledgeBase.errors.logoMultipleDots"), "error");
        fileLogoInputRef.current.value = "";
        return;
      }
      if (!allowedFormats.includes(file.type)) {
        showToast(t("knowledgeBase.errors.logoInvalidFormat"), "error");
        fileLogoInputRef.current.value = "";
        return;
      }
      if (file.size > MAX_LOGO_SIZE) {
        showToast(t("knowledgeBase.errors.logoTooLarge"));
        e.target.value = "";
        return;
      }
      setDeletedFiles((prev) =>
        prev.filter(({ fileName, type }) => !(fileName === file.name && type === "logo"))
      );
      setIsUploadingLogo(true);
      const res = await uploadLogo({ file, accessToken });
      if (res?.ok) setLogo(res.data.url);
    } catch (error) {
      showToast(t("knowledgeBase.errors.logoUploadError"), "error");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!logo) return;
    const fileName = logo.split("/").pop();
    try {
      const res = await deleteFile({ accessToken, fileName });
      if (res.ok) {
        showToast(t("knowledgeBase.errors.logoDeleteSuccess"), "success");
        setLogo("");
      } else showToast(t("knowledgeBase.errors.logoDeleteError"), "error");
    } catch (error) {
      console.log("خطا در حذف لوگو", error);
      showToast(t("knowledgeBase.errors.logoDeleteProblem"), "error");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const MAX_FILE_SIZE = Infinity;
  const handleFileChange = async (e) => {
    try {
      const newFiles = Array.from(e.target.files);
      const allowedFormats = [".pdf", ".txt", ".xlsx", ".csv", ".docx"];
      const dotErrorFiles = newFiles.filter((f) => f.name.split(".").length - 1 > 1);
      if (dotErrorFiles.length > 0) {
        showToast(t("knowledgeBase.errors.fileMultipleDots"), "error");
        fileInputRef.current.value = "";
        return;
      }
      const duplicateFiles = newFiles.filter((f) =>
        fileUrl.some((url) => url.split("/").pop() === f.name)
      );
      if (duplicateFiles.length > 0) {
        showToast(t("knowledgeBase.errors.duplicateFile"), "error");
        return;
      }
      setDeletedFiles((prev) =>
        prev.filter(({ fileName }) => !newFiles.some((f) => f.name === fileName))
      );
      const invalidFiles = newFiles.filter((f) => {
        const ext = `.${f.name.split(".").pop().toLowerCase()}`;
        return !allowedFormats.includes(ext);
      });
      if (invalidFiles.length > 0) {
        showToast(t("knowledgeBase.errors.invalidFormat"), "error");
        return;
      }
      const oversizedFiles = newFiles.filter((f) => f.size > MAX_FILE_SIZE);
      if (oversizedFiles.length > 0) {
        showToast(t("knowledgeBase.errors.fileTooLarge"), "error");
        return;
      }
      if (fileUrl.length + newFiles.length <= planLimitations?.files) {
        setFiles([...files, ...newFiles]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        for (const file of newFiles) {
          try {
            setUploadingFiles((prev) => prev + 1);
            const res = await uploadFile({ file, accessToken });
            if (res?.ok) {
              setFileUrl((prev) => [...prev, res.data.url]);
              setKnowledgeBaseFileUrls((prev) => [...prev, res.data.url]);
            }
          } catch (error) {
            if (error.response?.status == "413") {
              setTimeout(() => showToast(t("knowledgeBase.errors.fileTooLarge"), "error"), 500);
            }
          } finally {
            setUploadingFiles((prev) => prev - 1);
          }
        }
      } else {
        showToast(t("knowledgeBase.errors.tooManyFiles"), "error");
      }
    } catch (error) { }
  };

  const handleRemoveFile = async (fileName) => {
    setFileUrl((prev) => prev.filter((url) => url.split("/").pop() !== fileName));
    setFiles((prev) =>
      prev.filter((file) => {
        if (typeof file === "string") return file.split("/").pop() !== fileName;
        if (file.url) return file.url.split("/").pop() !== fileName;
        if (file.name) return file.name !== fileName;
        return true;
      })
    );
    setDeletedFiles((prev) => [...prev, { fileName, isDeleted: true, type: "file" }]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    try {
      const res = await deleteFile({ accessToken, fileName });
      if (res.ok) showToast(t("knowledgeBase.errors.fileDeleteSuccess", { name: fileName }), "success");
      else showToast(t("knowledgeBase.errors.fileDeleteError", { name: fileName }), "error");
    } catch (error) {
      showToast(t("knowledgeBase.errors.fileDeleteProblem", { name: fileName }), "error");
    } finally {
      window.location.reload();
    }
  };

  const handleDeleteAll = async () => {
    if (deletedFiles.length == 0) return;
    try {
      const responses = await Promise.all(
        deletedFiles.map(({ fileName }) => deleteFile({ accessToken, fileName }))
      );
      responses.forEach((res, i) => {
        if (!res.ok) console.log(`خطا در حذف ${deletedFiles[i].fileName}`);
      });
      setDeletedFiles([]);
    } catch (error) {
      console.log("خطا در حذف فایل‌ها", error);
    }
  };

  const handleSave = async () => {
    try {
      if (deletedFiles.length > 0) {
        await handleDeleteAll();
        setDeletedFiles([]);
      }
      if (customerPaymentEnabled && (!customerTokenPrice || Number(customerTokenPrice) <= 0)) {
        showToast(t("knowledgeBase.customerPayment.tokenPriceRequired"), "error");
        return;
      }
      if (!prompt.trim()) {
        showToast(t("knowledgeBase.prompt.required"), "error");
        return;
      }
      if (website != "") {
        if (/[\u0600-\u06FF]/.test(website)) {
          showToast(t("knowledgeBase.website.englishOnly"), "error");
          return;
        }
        const urlRegex = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
        if (!urlRegex.test(website)) {
          showToast(t("knowledgeBase.website.invalidFormat"), "error");
          return;
        }
      }
      const payload = {
        accessToken, prompt, website, logo,
        customerPaymentEnabled,
        customerTokenPrice: customerPaymentEnabled ? Number(customerTokenPrice) : null,
      };
      const res = status === "empty"
        ? await knowlageBase(payload)
        : await knowlageBaseEdit(payload);
      if (res?.ok) {
        showToast(t("knowledgeBase.toasts.saveSuccess"), "success");
        await getInfo();
      }
    } catch (error) {
      showToast(t("knowledgeBase.toasts.tryAgain"), "error");
      console.log(error);
    } finally {
      setTimeout(() => window.location.reload(), 2000);
    }
  };

  /* ---- Status config ---- */
  const statusConfig = {
    empty: { icon: Sparkles, title: t("knowledgeBase.statuses.empty.title"), text: t("knowledgeBase.statuses.empty.text"), classes: "from-amber-300/30 to-amber-400/10 border-amber-300/50 text-amber-800 dark:text-amber-200", iconColor: "text-amber-500" },
    pending: { icon: Clock, title: t("knowledgeBase.statuses.pending.title"), text: t("knowledgeBase.statuses.pending.text"), classes: "from-slate-300/40 to-slate-400/10 border-slate-400/40 text-slate-700 dark:text-slate-200", iconColor: "text-slate-500" },
    rejected: { icon: XCircle, title: t("knowledgeBase.statuses.rejected.title"), text: t("knowledgeBase.statuses.rejected.text"), classes: "from-rose-400/30 to-rose-500/10 border-rose-400/50 text-rose-800 dark:text-rose-200", iconColor: "text-rose-500" },
    accepted: { icon: CheckCircle2, title: t("knowledgeBase.statuses.accepted.title"), text: t("knowledgeBase.statuses.accepted.text"), classes: "from-emerald-400/30 to-teal-400/10 border-emerald-400/50 text-emerald-800 dark:text-emerald-200", iconColor: "text-emerald-500" },
  };
  const currentStatus = statusConfig[status] || null;

  if (loading) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <ProgressBar visible height="80" width="80" barColor="#14B8A6" borderColor="#FCD34D" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {/* ---------- Mobile backdrop ---------- */}
        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="
              lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30
              bg-slate-900/50 dark:bg-black/60
              backdrop-blur-sm
              transition-opacity duration-300
            "
            aria-hidden="true"
          />
        )}

        {/* ---------- Sidebar ---------- */}
        <aside
          className={`
            fixed top-[70px] bottom-0
            rtl:right-0 ltr:left-0
            w-64
            z-40

            transform transition-transform duration-300 ease-out
            ${isSidebarOpen
              ? "translate-x-0"
              : "rtl:translate-x-full ltr:-translate-x-full"
            }
            lg:!translate-x-0

            rtl:border-l ltr:border-r
            border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl
            shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden
            overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        {/* ---------- Main ---------- */}
        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="container-fluid px-3 sm:px-4 md:px-6 py-4">

              {/* Page Header */}
              <div className="mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <BookOpen size={18} className="text-amber-400" />
                  </span>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-amber-100">
                      {t("knowledgeBase.pageTitle")}
                    </h1>
                    <p className="text-xs sm:text-[13px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {t("knowledgeBase.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Banner */}
              {currentStatus && (
                <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-l ${currentStatus.classes} border backdrop-blur-md p-4 mb-4`}>
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-white/40 dark:bg-white/5 backdrop-blur-sm border border-current/20">
                      <currentStatus.icon size={18} className={currentStatus.iconColor} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm sm:text-base font-bold">{currentStatus.title}</h3>
                      <p className="text-xs sm:text-[13px] opacity-80 mt-0.5">{currentStatus.text}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Card */}
              <div className="relative rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">
                <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                  <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-300/10 blur-3xl dark:bg-amber-400/5" />
                  <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl dark:bg-teal-500/5" />
                </div>

                <div className="relative p-4 sm:p-6 space-y-5">

                  {/* Logo */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                      <ImageIcon size={15} className="text-teal-600/80 dark:text-teal-300/80" />
                      {t("knowledgeBase.logo.label")}
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input id="logo" ref={fileLogoInputRef} type="file" accept="image/jpeg, image/png, image/gif" onChange={handleLogoFileChange} className="hidden" />
                      <button type="button" onClick={() => fileLogoInputRef.current.click()}
                        className="group flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-teal-800 dark:text-amber-100 bg-white/70 dark:bg-white/5 border border-teal-500/25 dark:border-teal-400/25 backdrop-blur-md transition-all duration-300 hover:border-amber-300/60 hover:bg-amber-300/10 hover:scale-[1.02] active:scale-95">
                        <Upload size={15} className="text-teal-600 dark:text-teal-300 group-hover:text-amber-500 transition-colors" />
                        <span>{t("knowledgeBase.logo.selectButton")}</span>
                      </button>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">{t("knowledgeBase.logo.formats")}</span>
                    </div>
                    <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                      {t("knowledgeBase.logo.help", { size: planLimitations?.fileSize })}
                    </p>

                    {isUploadingLogo && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Loader2 size={16} className="animate-spin text-teal-500" />
                        {t("knowledgeBase.logo.uploading")}
                      </div>
                    )}

                    {logo && !isUploadingLogo && (
                      <div className="mt-3 rounded-xl bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md p-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img src={`${BASE_URL}${logo}`} alt={t("knowledgeBase.logo.alt")} className="w-16 h-16 object-cover rounded-lg border border-teal-500/20 shadow-sm" />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{t("knowledgeBase.logo.current")}</p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{logo.split("/").pop()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <a target="_blank" rel="noopener noreferrer" href={`${BASE_URL}${logo}`} download
                            className="h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/25 dark:border-teal-400/25 transition-all duration-300 hover:border-amber-300/60 hover:bg-amber-300/10 hover:scale-105 active:scale-95">
                            <Download size={14} />
                          </a>
                          <button onClick={handleLogoDelete}
                            className="h-9 w-9 flex items-center justify-center rounded-lg text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-400/40 transition-all duration-300 hover:bg-rose-500/20 hover:border-rose-400/70 hover:scale-105 active:scale-95">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* QR Code */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                      <QrCode
                        size={15}
                        className="text-teal-600/80 dark:text-teal-300/80"
                      />
                      {t("knowledgeBase.qr.label")}
                    </label>

                    {qrLoading ? (
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Loader2
                          size={16}
                          className="animate-spin text-teal-500"
                        />
                        {t("knowledgeBase.qr.loading")}
                      </div>
                    ) : qrData ? (
                      <div className="rounded-xl bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md p-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <img
                            src={qrData}
                            alt="QR Code"
                            className="w-[100px] h-[100px] object-contain rounded-lg border border-teal-500/20 bg-white p-1 shadow-sm"
                          />

                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">
                              {t("knowledgeBase.qr.title")}
                            </p>
                          </div>
                        </div>

                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={qrData}
                          download={`${username}-qr.png`}
                          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/25 dark:border-teal-400/25 transition-all duration-300 hover:border-amber-300/60 hover:bg-amber-300/10 hover:scale-105 active:scale-95"
                        >
                          <Download size={14} />
                        </a>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {t("knowledgeBase.qr.empty")}
                      </p>
                    )}
                  </div>

                  {/* Prompt */}
                  <div>
                    <label htmlFor="prompt" className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                      <FileText size={15} className="text-teal-600/80 dark:text-teal-300/80" />
                      {t("knowledgeBase.prompt.label")} <span className="text-rose-500 text-base">*</span>
                    </label>
                    <textarea id="prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows="4" required
                      className="w-full rounded-xl px-4 py-3 text-sm bg-white/70 dark:bg-slate-800/60 text-slate-800 dark:text-slate-100 border border-teal-500/20 dark:border-teal-400/20 focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60 transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-y min-h-[100px]" />
                    <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                      {t("knowledgeBase.prompt.hint")}
                    </p>
                  </div>

                  {/* Website */}
                  <div>
                    <label htmlFor="website" className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                      <Link2 size={15} className="text-teal-600/80 dark:text-teal-300/80" />
                      {t("knowledgeBase.website.label")}
                    </label>
                    <input id="website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." dir="ltr"
                      className="w-full rounded-xl px-4 py-2.5 text-sm text-left bg-white/70 dark:bg-slate-800/60 text-slate-800 dark:text-slate-100 border border-teal-500/20 dark:border-teal-400/20 focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60 transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-500" />
                    <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t("knowledgeBase.website.hint")}</p>
                  </div>

                  {/* Customer Payment */}
                  <div className="rounded-xl bg-white/50 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md p-3 sm:p-4">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input id="customerPaymentEnabled" type="checkbox" checked={customerPaymentEnabled}
                        onChange={(e) => { setCustomerPaymentEnabled(e.target.checked); if (!e.target.checked) setCustomerTokenPrice(""); }}
                        className="mt-0.5 w-4 h-4 cursor-pointer accent-teal-600 rounded" />
                      <div className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200 group-hover:text-amber-600 dark:group-hover:text-amber-200 transition-colors">
                          <CreditCard size={14} className="text-teal-600/80 dark:text-teal-300/80" />
                          {t("knowledgeBase.customerPayment.label")}
                        </span>
                        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                          {t("knowledgeBase.customerPayment.description")}
                        </p>
                      </div>
                    </label>
                    {customerPaymentEnabled && (
                      <div className="mt-3 pt-3 border-t border-teal-500/10 dark:border-teal-400/10">
                        <label htmlFor="customerTokenPrice" className="block text-[12px] font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                          {t("knowledgeBase.customerPayment.tokenPriceLabel")} <span className="text-rose-500">*</span>
                        </label>
                        <input id="customerTokenPrice" type="number" min="0" step="1" value={customerTokenPrice}
                          onChange={(e) => setCustomerTokenPrice(e.target.value)} placeholder={t("knowledgeBase.customerPayment.tokenPricePlaceholder")}
                          className="w-full rounded-xl px-4 py-2.5 text-sm tabular-nums bg-white/70 dark:bg-slate-800/60 text-slate-800 dark:text-slate-100 border border-teal-500/20 dark:border-teal-400/20 focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60 transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-500" />
                        <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                          {t("knowledgeBase.customerPayment.tokenPriceHint")}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Files */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                      <FileText size={15} className="text-teal-600/80 dark:text-teal-300/80" />
                      {t("knowledgeBase.files.label")}
                    </label>
                    <input id="files" accept=".pdf,.txt,.xlsx,.csv,.docx" ref={fileInputRef} type="file" multiple onChange={handleFileChange} className="hidden" />
                    <button type="button" onClick={() => fileInputRef.current.click()}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-teal-800 dark:text-amber-100 bg-white/70 dark:bg-white/5 border border-teal-500/25 dark:border-teal-400/25 backdrop-blur-md transition-all duration-300 hover:border-amber-300/60 hover:bg-amber-300/10 hover:scale-[1.02] active:scale-95">
                      <Upload size={15} className="text-teal-600 dark:text-teal-300 group-hover:text-amber-500 transition-colors" />
                      <span>{t("knowledgeBase.files.selectButton")}</span>
                    </button>

                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {t("knowledgeBase.files.allowedFormats")}
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {t("knowledgeBase.files.limits", {
                          count: planLimitations?.files,
                          size: planLimitations?.fileSize,
                        })}
                      </p>
                    </div>

                    {uploadingFiles > 0 && (
                      <div className="mt-3 flex items-center gap-2 rounded-xl bg-teal-500/10 border border-teal-400/30 px-3 py-2">
                        <Loader2 size={16} className="animate-spin text-teal-500 shrink-0" />
                        <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
                          {t("knowledgeBase.files.uploading", { count: uploadingFiles })}
                        </span>
                      </div>
                    )}

                    <div className="mt-3 space-y-2">
                      {knowledgeBaseFileUrls.filter((url) => url !== logo).map((url, index) => {
                        const fileName = url.split("/").pop();
                        const displayName = fileName.split(".")[0].slice(0, 30) + "." + fileName.split(".").pop();
                        const ext = fileName.split(".").pop().toUpperCase();
                        return (
                          <div key={index} className="group flex items-center justify-between gap-3 rounded-xl bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 backdrop-blur-md p-3 transition-all duration-200 hover:border-amber-300/40 hover:bg-amber-300/5">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40 text-[10px] font-extrabold text-amber-700 dark:text-amber-200">
                                {ext}
                              </span>
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{displayName}</p>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                  {t("knowledgeBase.files.fileNumber", { number: index + 1 })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <a target="_blank" rel="noopener noreferrer" href={`${BASE_URL}${url}`} download
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/25 dark:border-teal-400/25 transition-all duration-200 hover:border-amber-300/60 hover:bg-amber-300/10 hover:scale-105 active:scale-95">
                                <Download size={13} />
                              </a>
                              <button onClick={() => handleRemoveFile(fileName)}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-400/40 transition-all duration-200 hover:bg-rose-500/20 hover:border-rose-400/70 hover:scale-105 active:scale-95">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Save */}
                  <div className="pt-2 border-t border-teal-500/10 dark:border-teal-400/10">
                    <button onClick={handleSave}
                      className="group w-full relative overflow-hidden flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-bold text-white bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500 shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)] transition-all duration-300 hover:from-teal-600 hover:via-teal-500 hover:to-teal-400 hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)] hover:scale-[1.01] active:scale-[0.99]">
                      <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      <span className="relative flex h-6 w-6 items-center justify-center rounded-lg bg-white/15 border border-white/20">
                        <Save size={13} />
                      </span>
                      <span className="relative">
                        {status == "empty" || status == "rejected"
                          ? t("knowledgeBase.actions.submit")
                          : t("knowledgeBase.actions.update")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer داخل اسکرول‌کانتینر */}
              <Footer />
            </div>
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const CreatingKnowledgeBase = () => (
  <SidebarProvider>
    <CreatingKnowledgeBaseInner />
  </SidebarProvider>
);

export default CreatingKnowledgeBase;